import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EinvoiceSettingInputSchema, type EinvoiceSettingInput } from '@app/shared';
import { AddressInput } from '../../components/AddressInput';
import { useEinvoiceSetting, useUpdateEinvoice } from './einvoiceApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function EinvoiceForm(): JSX.Element {
  const { data, isLoading, isError, error } = useEinvoiceSetting();
  const updateMutation = useUpdateEinvoice();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EinvoiceSettingInput>({
    resolver: zodResolver(EinvoiceSettingInputSchema),
    defaultValues: {
      enabled: false,
      environment: 'test',
      sellerTaxId: '',
      sellerName: '',
      address: '',
      phone: '',
      fax: '',
      defaultTaxRate: 0.05,
      merchantId: '',
      hashKey: '',
      hashIV: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        enabled: data.enabled,
        environment: data.environment,
        sellerTaxId: data.sellerTaxId,
        sellerName: data.sellerName,
        address: data.address,
        phone: data.phone,
        fax: data.fax,
        defaultTaxRate: data.defaultTaxRate,
        merchantId: data.merchantId,
        // 不回填明文；留空 = 保留原金鑰
        hashKey: '',
        hashIV: '',
      });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: EinvoiceSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset({ ...values, hashKey: '', hashIV: '' });
      },
    });
  };

  const serverErrorMsg = (err: unknown): string => getApiErrorMessage(err, '未知錯誤');

  const environment = watch('environment');

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">電子發票（ezPay）</h2>
      <p className="text-sm text-slate-600">
        透過 ezPay 電子發票平台開立，字軌配號與上傳財政部由 ezPay 處理。商店代號、HashKey、HashIV 請至 ezPay 商店後台複製；測試與正式環境的金鑰不共用。HashKey / HashIV 以 AES-256-GCM 加密儲存，留空代表保留原值。
      </p>

      {environment === 'production' ? (
        <div role="alert" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          ⚠️ 目前為「正式」環境，會在 ezPay 正式平台開立真實發票並上傳財政部，無法刪除。請確認非測試用途。
        </div>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用電子發票開立
        </label>

        <Row label="開立環境" error={errors.environment?.message}>
          <select {...register('environment')} className={`${inputCls} w-48`}>
            <option value="test">ezPay 測試平台</option>
            <option value="production">ezPay 正式平台</option>
          </select>
        </Row>

        <Row label="賣方統一編號" error={errors.sellerTaxId?.message}>
          <input
            {...register('sellerTaxId')}
            className={`${inputCls} w-48`}
            inputMode="numeric"
            placeholder="12345678"
          />
        </Row>
        <Row label="賣方營業人名稱" error={errors.sellerName?.message}>
          <input {...register('sellerName')} className={inputCls} placeholder="易信實業有限公司" />
        </Row>
        {/*
          地址 / 電話 / 傳真原本兼作請款、對帳文件抬頭上的「我方公司」資料
          (REQ-20260817003)。REQ-20260817004 起抬頭改由「設定 → 公司基本資料」提供,
          這三格只剩下電子發票自己在用(以及公司基本資料還沒存過時的 fallback 來源),
          所以**不能刪**:欄位是 `.default('')`,拿掉輸入框的話管理員每存一次
          電子發票設定就會把既有值洗成空字串,連 fallback 都一起沒了。
        */}
        <p className="text-xs text-slate-500">
          以下三格供電子發票使用;單據抬頭的「我方公司」請改在「公司基本資料」維護。
        </p>
        <AddressRow label="賣方地址" error={errors.address?.message}>
          <AddressInput
            label="賣方地址"
            value={watch('address')}
            onChange={(next) => setValue('address', next, { shouldDirty: true })}
            inputClassName={inputCls}
            placeholder="台中市西區某某路 1 號"
          />
        </AddressRow>
        <Row label="賣方電話" error={errors.phone?.message}>
          <input {...register('phone')} className={`${inputCls} w-64`} placeholder="04-1234-5678" />
        </Row>
        <Row label="賣方傳真" error={errors.fax?.message}>
          <input {...register('fax')} className={`${inputCls} w-64`} placeholder="04-1234-5679" />
        </Row>
        <Row label="預設稅率" error={errors.defaultTaxRate?.message}>
          <input
            type="number"
            step="0.0001"
            {...register('defaultTaxRate', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>

        <Row label="ezPay 商店代號（MerchantID）" error={errors.merchantId?.message}>
          <input {...register('merchantId')} className={`${inputCls} w-48`} autoComplete="off" />
        </Row>
        <Row
          label={`HashKey（32 碼）${data?.hasHashKey ? '（已設定，留空保留原值）' : ''}`}
          error={errors.hashKey?.message}
        >
          <input
            type="password"
            {...register('hashKey')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasHashKey ? '（留空保留原值）' : '（未設定）'}
          />
        </Row>
        <Row
          label={`HashIV（16 碼）${data?.hasHashIV ? '（已設定，留空保留原值）' : ''}`}
          error={errors.hashIV?.message}
        >
          <input
            type="password"
            {...register('hashIV')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasHashIV ? '（留空保留原值）' : '（未設定）'}
          />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            儲存失敗：{serverErrorMsg(updateMutation.error)}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">
            {savedFlash}
          </div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * 地址列(REQ-20260901004)。與 `Row` 同外觀,但用 `div` + `span` 而不是包住 children 的
 * `<label>` —— 郵遞區號 / 地址是兩個 input,包在同一個 label 裡會讓 getByLabelText
 * 抓到多個元素;標籤關聯改由各 input 自己的 aria-label 承接。
 */
function AddressRow({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const inputCls =
  'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
